import Widget from "./Widget";
import { gql } from "@apollo/client";
import Text from "@src/lib/ui/Text";

import { Skeleton } from "@src/lib/ui/Skeleton";
import { useMemo } from "react";
import {
  HtmlTable,
  HtmlTableBody,
  HtmlTableCell,
  HtmlTableHead,
  HtmlTableHeader,
  HtmlTableRow,
} from "@src/lib/ui/HtmlTable";
import { ManagerLeaderboardItem_WidgetFragment } from "./ActiveManagersWidget.generated";
import { ActiveAndTotalManagersQuery } from "./ActiveManagersWidget.generated";
import { useActiveAndTotalManagersQuery } from "./ActiveManagersWidget.generated";
import { DateTime } from "luxon";
import CalendarOutlineIcon from "@src/lib/icons/18px/calendar-outline";
import EmptyWidgetContent from "./EmptyWidgetContent";

const ActiveManagersWidget: React.FC = () => {
  const variables = useMemo(() => {
    const activeSince = DateTime.local().minus({ days: 30 }).toISO();
    return { activeSince };
  }, []);
  const { data, loading } = useActiveAndTotalManagersQuery({
    variables,
  });

  if (!loading && data?.TotalManagers?.totalCount === 0) {
    return (
      <Widget title="Active Managers Past 30 Days" Icon={CalendarOutlineIcon}>
        <EmptyWidgetContent
          illustration={
            <CalendarOutlineIcon className="size-8 text-muted-foreground" />
          }
          description="You'll keep track of your top performing managers here"
        />
      </Widget>
    );
  }

  return (
    <Widget
      title="Active Managers Past 30 Days"
      Icon={CalendarOutlineIcon}
      href={{ pathname: "/reporting/manager-leaderboard" }}
    >
      <div className="flex max-h-[300px] w-full flex-col overflow-y-auto">
        <div className="flex flex-col items-start">
          <div className="mb-6 flex items-end self-stretch">
            {loading ? (
              <Skeleton className="h-[28px] w-[60px] rounded-md" />
            ) : (
              <Text variant="display-bold" className="leading-7">
                {data?.ActiveManagers?.totalCount}
              </Text>
            )}
            <Text variant="fine-print-title" className="text-gray-7">
              &nbsp;out of&nbsp;
            </Text>
            {loading ? (
              <Skeleton className="h-[16px] w-[40px] rounded-md" />
            ) : (
              <Text variant="fine-print-title" className="text-gray-10">
                {data?.TotalManagers?.totalCount}
              </Text>
            )}
          </div>
        </div>
        <div className="mt-auto">
          {loading ? (
            <ManagerLeaderboardWidgetTableSkeleton />
          ) : (
            data?.AssignmentsCompleted && (
              <ManagerLeaderboardWidgetTable data={data.AssignmentsCompleted} />
            )
          )}
        </div>
      </div>
    </Widget>
  );
};

export function ManagerLeaderboardWidgetTable({
  data,
}: {
  data: ActiveAndTotalManagersQuery["AssignmentsCompleted"];
}): React.ReactElement {
  const getMedalEmoji = (index: number): string => {
    switch (index) {
      case 0:
        return "🥇";
      case 1:
        return "🥈";
      case 2:
        return "🥉";
      default:
        return `${index + 1}`;
    }
  };

  return (
    <HtmlTable className="w-full">
      <HtmlTableHeader>
        <HtmlTableRow className="border-none bg-gray-1">
          <HtmlTableHead className="rounded-tl-md text-gray-7">
            Manager
          </HtmlTableHead>
          <HtmlTableHead className="rounded-tr-md text-right text-gray-7">
            Team Completed Assignments
          </HtmlTableHead>
        </HtmlTableRow>
      </HtmlTableHeader>
      <HtmlTableBody>
        {data?.objects
          ?.slice(0, 5)
          .map?.(
            (
              leaderboardItem: ManagerLeaderboardItem_WidgetFragment,
              index: number,
            ) => {
              if (!leaderboardItem) {
                return null;
              }
              return (
                <HtmlTableRow key={leaderboardItem.id}>
                  <HtmlTableCell>
                    <Text variant="body-description" className="mr-2">
                      {getMedalEmoji(index)}
                    </Text>
                    <Text variant="compact-description">
                      {leaderboardItem.managerName}
                    </Text>
                  </HtmlTableCell>
                  <HtmlTableCell className="text-right">
                    <Text variant="compact-description">
                      {leaderboardItem.teamAssignmentsCompleted}
                    </Text>
                  </HtmlTableCell>
                </HtmlTableRow>
              );
            },
          )}
      </HtmlTableBody>
    </HtmlTable>
  );
}

// Add this new component for the table skeleton
const ManagerLeaderboardWidgetTableSkeleton: React.FC = () => (
  <HtmlTable>
    <HtmlTableHeader>
      <HtmlTableRow className="border-none bg-gray-1">
        <HtmlTableHead className="w-[200px] rounded-tl-md text-gray-7">
          Manager
        </HtmlTableHead>
        <HtmlTableHead className="rounded-tr-md text-right text-gray-7">
          Team Completed Assignments
        </HtmlTableHead>
      </HtmlTableRow>
    </HtmlTableHeader>
    <HtmlTableBody>
      {[1, 2, 3].map((index) => (
        <HtmlTableRow key={index}>
          <HtmlTableCell>
            <Skeleton className="h-[20px] w-[150px] rounded-md" />
          </HtmlTableCell>
          <HtmlTableCell className="text-right">
            <Skeleton className="ml-auto h-[20px] w-[40px] rounded-md" />
          </HtmlTableCell>
        </HtmlTableRow>
      ))}
    </HtmlTableBody>
  </HtmlTable>
);

gql`
  fragment ManagerLeaderboardItem_Widget on ManagerLeaderboardItem {
    id
    managerId
    managerName
    teamAssignmentsCompleted
  }
  query ActiveAndTotalManagers($activeSince: DateTime!) {
    ActiveManagers: People(
      input: {
        activeSince: { value: $activeSince }
        userTypes: { value: [MANAGER] }
      }
    ) {
      totalCount
    }
    TotalManagers: People(input: { userTypes: { value: [MANAGER] } }) {
      totalCount
    }
    AssignmentsCompleted: ManagerLeaderboardPaginated(
      input: {
        window: MONTH
        sort: { column: "teamAssignmentsCompleted", descending: true }
      }
      pagination: { limit: 3, offset: 0 }
    ) {
      objects {
        ...ManagerLeaderboardItem_Widget
      }
    }
  }
`;

export default ActiveManagersWidget;
